import React, { useContext, useEffect, useMemo, useRef, useState } from 'react';
import axios from 'axios';
import ApiUrl from '../../utils/config';
import {
  AgeCategoryInTournament,
  ConfigClient,
  Gender,
  ParticipantClient,
  ParticipantDto,
  Person,
  PersonClient,
  Tournament,
  WeightCategoryInTournament,
} from '../../utils/apiClient';
import AppContext from '../../AppContext';
import { NotificationType } from '../../common/types';
import { ReactSelectDefault } from '../../common/components/ReactSelectDefault/ReactSelect';
import { handleError } from '../../utils';
import { formatWeightCategoryKgForPrint, formatWeightCategoryLabel, resolveWeightCategory } from '../../utils/categoryResolver';
import { formatGenderLabel } from '../../utils/genderCategoryUtils';
import {
  QuickAddWeighInEntryRequest,
  WeighInProtocolEntryDto,
  WeighInProtocolModel,
  weighInProtocolClient,
} from '../../utils/weighInProtocolApi';
import './WeighInModal.scss';

interface WeighInModalProps {
  isOpen: boolean;
  onClose: () => void;
  tournament: Tournament;
  ageCategory: AgeCategoryInTournament;
  weightCategory: WeightCategoryInTournament;
  onParticipantsUpdated: () => void;
  onProtocolChanged?: () => void;
}

interface ConfirmState {
  entryKey: string;
  newWeight: number;
  targetCategory: WeightCategoryInTournament;
}

type EditedEntry = WeighInProtocolEntryDto & { _key: string; coachId?: number };

const formatCoachName = (coach: Person): string =>
  `${coach.lastName} ${coach.firstName} ${coach.patronymic || ''}`.trim();

const DEFAULT_RANKS = [
  'Б/р', '3 юн.', '2 юн.', '1 юн.',
  '3 разряд', '2 разряд', '1 разряд',
  'КМС', 'МС', 'МСМК', 'ЗМС', 'Другое',
];

const entryKey = (e: WeighInProtocolEntryDto): string =>
  e.id != null ? `id-${e.id}` : `pid-${e.participantId}`;

const fullName = (e: WeighInProtocolEntryDto): string =>
  `${e.lastName ?? ''} ${e.firstName ?? ''} ${e.patronymic ?? ''}`.trim();

const matchesNameSearch = (e: WeighInProtocolEntryDto, query: string): boolean => {
  const trimmed = query.trim();
  if (!trimmed) return true;
  const tokens = trimmed.toLowerCase().split(/\s+/).filter(Boolean);
  const lastName = (e.lastName ?? '').toLowerCase();
  const firstName = (e.firstName ?? '').toLowerCase();
  const patronymic = (e.patronymic ?? '').toLowerCase();
  const full = `${lastName} ${firstName} ${patronymic}`.trim();
  return tokens.every(
    (token) =>
      lastName.includes(token) ||
      firstName.includes(token) ||
      patronymic.includes(token) ||
      full.includes(token)
  );
};

const toDateInput = (d?: string | Date | null): string => {
  if (!d) return '';
  const dt = typeof d === 'string' ? new Date(d) : d;
  if (Number.isNaN(dt.getTime())) return '';
  return dt.toISOString().slice(0, 10);
};

type ProtocolHeaderState = {
  participantCategoryLabel: string;
  weightCategoryKg: string;
  protocolDate: string;
};

const WEIGH_IN_PROTOCOL_TITLE_PREFIX = 'Протокол взвешивания участников соревнования';

const wrapInGuillemets = (text: string): string => {
  const name = text.trim();
  if (!name) return name;
  if (name.startsWith('«') && name.endsWith('»')) return name;
  return `«${name}»`;
};

const buildDefaultProtocolTitle = (tournament: Tournament | string) => {
  if (typeof tournament !== 'string') {
    const custom = tournament.weighInProtocolTitle?.trim();
    if (custom) return custom;
    return buildDefaultProtocolTitle(tournament.name ?? '');
  }

  const name = tournament.trim();
  if (!name) return WEIGH_IN_PROTOCOL_TITLE_PREFIX;
  return `${WEIGH_IN_PROTOCOL_TITLE_PREFIX} ${wrapInGuillemets(name)}`;
};

const resolveProtocolTitle = (
  savedLabel: string | undefined,
  tournament: Tournament,
  legacyAgeLabel?: string
) => {
  const defaultTitle = buildDefaultProtocolTitle(tournament);
  const saved = savedLabel?.trim();
  if (!saved) return defaultTitle;

  const tournamentName = tournament.name?.trim() ?? '';
  const ageLabel = legacyAgeLabel?.trim();
  const autoDefault = buildDefaultProtocolTitle(tournamentName);
  const legacyTitle = tournamentName
    ? `${WEIGH_IN_PROTOCOL_TITLE_PREFIX} ${tournamentName}`
    : WEIGH_IN_PROTOCOL_TITLE_PREFIX;

  if (
    saved === tournamentName ||
    saved === ageLabel ||
    saved === autoDefault ||
    saved === legacyTitle
  ) {
    return defaultTitle;
  }

  return saved;
};

const buildDefaultHeader = (
  weightCategory: WeightCategoryInTournament,
  tournament: Tournament
): ProtocolHeaderState => ({
  participantCategoryLabel: buildDefaultProtocolTitle(tournament),
  weightCategoryKg: formatWeightCategoryKgForPrint(
    weightCategory,
    tournament.weightCategories ?? []
  ),
  protocolDate: toDateInput(tournament.startDate),
});

const collectOrganizations = (...sources: (string | undefined | null)[]) => {
  const set = new Set<string>();
  for (const source of sources) {
    const value = source?.trim();
    if (value) set.add(value);
  }
  return [...set].sort((a, b) => a.localeCompare(b, 'ru'));
};

const emptyQuickAdd = (): QuickAddWeighInEntryRequest => ({
  tournamentId: 0,
  ageCategoryInTournamentId: 0,
  weightCategoryInTournamentId: 0,
  lastName: '',
  firstName: '',
  patronymic: '',
  birthDate: '',
  rank: 'Б/р',
  weight: 0,
  organization: '',
  gender: Gender.Male,
});

export const WeighInModal: React.FC<WeighInModalProps> = ({
  isOpen,
  onClose,
  tournament,
  ageCategory,
  weightCategory,
  onParticipantsUpdated,
  onProtocolChanged,
}) => {
  const appContext = useContext(AppContext);
  const participantClient = new ParticipantClient(ApiUrl, axios);
  const configClient = new ConfigClient(ApiUrl, axios);
  const personClient = new PersonClient(ApiUrl, axios);

  const [entries, setEntries] = useState<EditedEntry[]>([]);
  const [coaches, setCoaches] = useState<Person[]>([]);
  const [organizationOptions, setOrganizationOptions] = useState<string[]>([]);
  const [rowErrors, setRowErrors] = useState<Record<string, string>>({});
  const [savingKey, setSavingKey] = useState<string | null>(null);
  const [bulkSaving, setBulkSaving] = useState(false);
  const [confirm, setConfirm] = useState<ConfirmState | null>(null);
  const [loading, setLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [rankOptions, setRankOptions] = useState<string[]>(DEFAULT_RANKS);
  const [showQuickAdd, setShowQuickAdd] = useState(false);
  const [quickAdd, setQuickAdd] = useState<QuickAddWeighInEntryRequest>(emptyQuickAdd());
  const [quickAddCoachId, setQuickAddCoachId] = useState<number | undefined>(undefined);
  const [importing, setImporting] = useState(false);
  const [printing, setPrinting] = useState(false);
  const [participantCategoryLabel, setParticipantCategoryLabel] = useState('');
  const [headerWeightKg, setHeaderWeightKg] = useState('');
  const [headerDate, setHeaderDate] = useState('');
  const [savedHeader, setSavedHeader] = useState<ProtocolHeaderState>({
    participantCategoryLabel: '',
    weightCategoryKg: '',
    protocolDate: '',
  });
  const [savingHeader, setSavingHeader] = useState(false);
  const headerDirtyRef = useRef(false);
  const searchInputRef = useRef<HTMLInputElement>(null);

  const applyHeaderFromProtocol = (protocol: WeighInProtocolModel, force = false) => {
    if (headerDirtyRef.current && !force) return;

    const header: ProtocolHeaderState = {
      participantCategoryLabel: resolveProtocolTitle(
        protocol.participantCategoryLabel,
        tournament,
        ageCategory.name
      ),
      weightCategoryKg: protocol.weightCategoryKg?.trim()
        || formatWeightCategoryKgForPrint(weightCategory, tournament.weightCategories ?? []),
      protocolDate: toDateInput(protocol.protocolDate ?? tournament.startDate),
    };
    setParticipantCategoryLabel(header.participantCategoryLabel);
    setHeaderWeightKg(header.weightCategoryKg);
    setHeaderDate(header.protocolDate);
    setSavedHeader(header);
  };

  const applyEntriesFromProtocol = (protocol: WeighInProtocolModel, coachList: Person[] = coaches) => {
    setEntries(
      protocol.entries.map((e) => ({
        ...e,
        birthDate: toDateInput(e.birthDate),
        _key: entryKey(e),
        coachId: resolveCoachIdFromName(e.coachName, coachList),
      }))
    );
    setRowErrors({});
  };

  const applyProtocol = (
    protocol: WeighInProtocolModel,
    coachList: Person[] = coaches,
    includeHeader = false
  ) => {
    applyEntriesFromProtocol(protocol, coachList);
    if (includeHeader) {
      applyHeaderFromProtocol(protocol, true);
    }
    onProtocolChanged?.();
  };

  const resolveCoachIdFromName = (coachName?: string, list: Person[] = coaches): number | undefined => {
    if (!coachName?.trim() || !list.length) return undefined;
    const normalized = coachName.trim().toLowerCase();
    return list.find((c) => formatCoachName(c).toLowerCase() === normalized)?.id;
  };

  const coachOptions = useMemo(
    () =>
      coaches.map((coach) => ({
        value: coach.id!,
        label: formatCoachName(coach),
      })),
    [coaches]
  );

  const organizationSelectOptions = useMemo(
    () =>
      collectOrganizations(
        ...organizationOptions,
        ...entries.map((entry) => entry.organization),
        quickAdd.organization
      ).map((organization) => ({
        value: organization,
        label: organization,
      })),
    [organizationOptions, entries, quickAdd.organization]
  );

  const loadProtocol = async (coachList?: Person[]) => {
    setLoading(true);
    try {
      const protocol = await weighInProtocolClient.getProtocol(
        tournament.id!,
        ageCategory.id!,
        weightCategory.id!
      );
      applyProtocol(protocol, coachList ?? coaches, true);
    } catch (ex) {
      handleError(appContext!.setNotificationData, ex);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!isOpen) return;
    headerDirtyRef.current = false;
    setSearchQuery('');
    setShowQuickAdd(false);
    const defaultHeader = buildDefaultHeader(weightCategory, tournament);
    const defaultTitle = buildDefaultProtocolTitle(tournament);
    setParticipantCategoryLabel(defaultTitle || defaultHeader.participantCategoryLabel);
    setHeaderWeightKg(defaultHeader.weightCategoryKg);
    setHeaderDate(defaultHeader.protocolDate);
    setSavedHeader({
      ...defaultHeader,
      participantCategoryLabel: defaultTitle || defaultHeader.participantCategoryLabel,
    });
    setQuickAdd({
      ...emptyQuickAdd(),
      tournamentId: tournament.id!,
      ageCategoryInTournamentId: ageCategory.id!,
      weightCategoryInTournamentId: weightCategory.id!,
      gender: (ageCategory.gender ?? weightCategory.gender ?? Gender.Male) as Gender,
    });

    const load = async () => {
      try {
        const config = await configClient.getConfig();
        if (config.rankOptions?.length) setRankOptions(config.rankOptions);
      } catch {
        /* fallback to defaults */
      }
      let loadedCoaches: Person[] = [];
      try {
        const persons = await personClient.getPersons();
        loadedCoaches = persons.filter((p) => p.personRoles?.some((r) => r.role?.name === 'Тренер'));
        setCoaches(loadedCoaches);
      } catch {
        setCoaches([]);
      }
      try {
        const participants = await participantClient.getParticipants(tournament.id!);
        setOrganizationOptions(collectOrganizations(...participants.map((p) => p.organization)));
      } catch {
        setOrganizationOptions([]);
      }
      await loadProtocol(loadedCoaches);
    };
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, tournament.id, ageCategory.id, weightCategory.id]);

  useEffect(() => {
    if (!coaches.length || !entries.length) return;
    setEntries((prev) =>
      prev.map((e) => ({
        ...e,
        coachId: e.coachId ?? resolveCoachIdFromName(e.coachName, coaches),
      }))
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [coaches]);

  useEffect(() => {
    if (!isOpen || loading) return;
    const timer = window.setTimeout(() => searchInputRef.current?.focus(), 50);
    return () => window.clearTimeout(timer);
  }, [isOpen, loading, ageCategory.id, weightCategory.id]);

  const filteredEntries = useMemo(
    () => entries.filter((e) => matchesNameSearch(e, searchQuery)),
    [entries, searchQuery]
  );

  const categoryLabel = useMemo(
    () => formatWeightCategoryLabel(weightCategory, tournament.weightCategories ?? []),
    [weightCategory, tournament.weightCategories]
  );

  const categoryGender = weightCategory.gender ?? ageCategory.gender ?? null;

  const inCurrentRange = (w: number) =>
    resolveWeightCategory(w, tournament.weightCategories ?? [], categoryGender)?.id === weightCategory.id;

  const findMatchingCategory = (w: number): WeightCategoryInTournament | undefined =>
    resolveWeightCategory(w, tournament.weightCategories ?? [], categoryGender);

  const setRowError = (key: string, msg: string | undefined) => {
    setRowErrors((prev) => {
      const next = { ...prev };
      if (msg) next[key] = msg;
      else delete next[key];
      return next;
    });
  };

  const updateEntry = (key: string, patch: Partial<EditedEntry>) => {
    setEntries((prev) =>
      prev.map((e) => (e._key === key ? { ...e, ...patch, _key: key } : e))
    );
  };

  const buildSaveRequest = (list: EditedEntry[]) => ({
    tournamentId: tournament.id!,
    ageCategoryInTournamentId: ageCategory.id!,
    weightCategoryInTournamentId: weightCategory.id!,
    entries: list.map(({ _key, coachId, ...e }) => ({
      ...e,
      birthDate: e.birthDate.includes('T') ? e.birthDate : `${e.birthDate}T00:00:00.000Z`,
    })),
  });

  const reassignParticipant = async (
    entry: EditedEntry,
    newWeight: number,
    targetCategoryId: number
  ) => {
    const existing = await participantClient.getParticipant(entry.participantId!);
    await participantClient.updateParticipant(
      new ParticipantDto({
        id: entry.participantId,
        personId: existing.personId!,
        tournamentId: tournament.id!,
        weight: newWeight,
        organization: entry.organization ?? '',
        rank: entry.rank,
        ageCategoryInTournamentId: ageCategory.id,
        weightCategoryInTournamentId: targetCategoryId,
        coachId: existing.coachId,
      })
    );
    if (entry.id) {
      await weighInProtocolClient.removeEntry(entry.id);
    }
    setEntries((prev) => prev.filter((e) => e._key !== entry._key));
    onParticipantsUpdated();
  };

  const persistEntry = async (entry: EditedEntry, newWeight: number, removeAfter = false) => {
    const key = entry._key;
    setSavingKey(key);
    setRowError(key, undefined);
    try {
      const updated = { ...entry, weight: newWeight };
      const protocol = await weighInProtocolClient.saveEntries(
        buildSaveRequest([{ ...updated, sortOrder: entry.sortOrder }])
      );
      if (removeAfter) {
        if (entry.id) await weighInProtocolClient.removeEntry(entry.id);
        setEntries((prev) => prev.filter((e) => e._key !== key));
      } else {
        applyProtocol(protocol);
      }
      onParticipantsUpdated();
    } catch (ex) {
      handleError(appContext!.setNotificationData, ex, 'Не удалось сохранить запись');
    } finally {
      setSavingKey(null);
    }
  };

  const handleSaveRow = (entry: EditedEntry) => {
    const newWeight = entry.weight;
    if (isNaN(newWeight) || newWeight <= 0 || newWeight > 200) {
      setRowError(entry._key, 'Введите вес в диапазоне 0–200 кг');
      return;
    }
    if (!entry.lastName.trim() || !entry.firstName.trim()) {
      setRowError(entry._key, 'Укажите фамилию и имя');
      return;
    }
    if (!entry.birthDate) {
      setRowError(entry._key, 'Укажите дату рождения');
      return;
    }
    if (!entry.rank) {
      setRowError(entry._key, 'Укажите разряд');
      return;
    }

    if (inCurrentRange(newWeight)) {
      persistEntry(entry, newWeight);
      return;
    }

    const target = findMatchingCategory(newWeight);
    if (!target) {
      const available = tournament.weightCategories
        ?.map((wc) => formatWeightCategoryLabel(wc, tournament.weightCategories ?? []))
        .join(', ');
      setRowError(entry._key, `Нет подходящей весовой категории для ${newWeight} кг. Доступные: ${available} кг`);
      return;
    }

    setConfirm({ entryKey: entry._key, newWeight, targetCategory: target });
  };

  const handleConfirmReassign = async () => {
    if (!confirm) return;
    const entry = entries.find((e) => e._key === confirm.entryKey);
    if (!entry?.participantId || !confirm.targetCategory.id) {
      setConfirm(null);
      return;
    }
    setSavingKey(confirm.entryKey);
    try {
      await reassignParticipant(entry, confirm.newWeight, confirm.targetCategory.id);
    } catch (ex) {
      handleError(appContext!.setNotificationData, ex, 'Не удалось перевести участника');
    } finally {
      setSavingKey(null);
      setConfirm(null);
    }
  };

  const handleSaveAll = async () => {
    setBulkSaving(true);
    try {
      const protocol = await weighInProtocolClient.saveEntries(buildSaveRequest(entries));
      applyProtocol(protocol);
      onParticipantsUpdated();
      appContext?.setNotificationData?.({ message: 'Протокол сохранён', type: NotificationType.SUCCESS });
    } catch (ex) {
      handleError(appContext!.setNotificationData, ex, 'Не удалось сохранить протокол');
    } finally {
      setBulkSaving(false);
    }
  };

  const handleImport = async () => {
    setImporting(true);
    try {
      const protocol = await weighInProtocolClient.importFromRegistered(
        tournament.id!,
        ageCategory.id!,
        weightCategory.id!
      );
      applyProtocol(protocol);
      onParticipantsUpdated();
    } catch (ex) {
      handleError(appContext!.setNotificationData, ex, 'Не удалось импортировать участников');
    } finally {
      setImporting(false);
    }
  };

  const handleQuickAdd = async () => {
    if (!quickAdd.lastName.trim() || !quickAdd.firstName.trim()) {
      appContext?.setNotificationData?.({ message: 'Укажите фамилию и имя', type: NotificationType.ERROR });
      return;
    }
    if (!quickAdd.birthDate) {
      appContext?.setNotificationData?.({ message: 'Укажите дату рождения', type: NotificationType.ERROR });
      return;
    }
    if (!quickAdd.weight || quickAdd.weight <= 0) {
      appContext?.setNotificationData?.({ message: 'Укажите вес', type: NotificationType.ERROR });
      return;
    }
    if (!inCurrentRange(quickAdd.weight)) {
      appContext?.setNotificationData?.({
        message: 'Вес не попадает в текущую категорию',
        type: NotificationType.ERROR,
      });
      return;
    }

    setSavingKey('quick-add');
    const prevIds = new Set(entries.map((e) => e.id).filter(Boolean));
    const selectedCoach = quickAddCoachId ? coaches.find((c) => c.id === quickAddCoachId) : undefined;
    try {
      const protocol = await weighInProtocolClient.quickAddEntry({
        ...quickAdd,
        lastName: quickAdd.lastName.trim(),
        firstName: quickAdd.firstName.trim(),
        patronymic: quickAdd.patronymic?.trim() || undefined,
        organization: quickAdd.organization?.trim() || undefined,
        coachId: quickAddCoachId,
        coachName: selectedCoach ? formatCoachName(selectedCoach) : undefined,
        birthDate: quickAdd.birthDate.includes('T')
          ? quickAdd.birthDate
          : `${quickAdd.birthDate}T00:00:00.000Z`,
      });

      // Добавляем нового участника в начало списка
      const newMapped: EditedEntry[] = protocol.entries.map((e) => ({
        ...e,
        birthDate: toDateInput(e.birthDate),
        _key: entryKey(e),
        coachId: resolveCoachIdFromName(e.coachName, coaches),
      }));
      const newEntry = newMapped.find((e) => e.id && !prevIds.has(e.id));
      if (newEntry && !newEntry.coachId && quickAddCoachId) {
        newEntry.coachId = quickAddCoachId;
      }
      const rest = newMapped.filter((e) => e.id !== newEntry?.id);
      setEntries(newEntry ? [newEntry, ...rest] : newMapped);
      setRowErrors({});

      setQuickAdd({
        ...emptyQuickAdd(),
        tournamentId: tournament.id!,
        ageCategoryInTournamentId: ageCategory.id!,
        weightCategoryInTournamentId: weightCategory.id!,
        gender: (ageCategory.gender ?? weightCategory.gender ?? Gender.Male) as Gender,
      });
      setQuickAddCoachId(undefined);
      setShowQuickAdd(false);
      onParticipantsUpdated();
    } catch (ex) {
      handleError(appContext!.setNotificationData, ex, 'Не удалось добавить участника');
    } finally {
      setSavingKey(null);
    }
  };

  const handleRemove = async (entry: EditedEntry) => {
    if (!entry.id) return;
    if (!window.confirm(`Удалить ${fullName(entry)} из протокола?`)) return;
    setSavingKey(entry._key);
    try {
      const protocol = await weighInProtocolClient.removeEntry(entry.id);
      applyProtocol(protocol);
    } catch (ex) {
      handleError(appContext!.setNotificationData, ex);
    } finally {
      setSavingKey(null);
    }
  };

  const persistProtocolHeader = async (showSuccess = false): Promise<boolean> => {
    const nextHeader: ProtocolHeaderState = {
      participantCategoryLabel: participantCategoryLabel.trim(),
      weightCategoryKg: headerWeightKg.trim(),
      protocolDate: headerDate,
    };

    if (!nextHeader.participantCategoryLabel) {
      appContext?.setNotificationData?.({
        message: 'Укажите название турнира для шапки протокола',
        type: NotificationType.ERROR,
      });
      setParticipantCategoryLabel(savedHeader.participantCategoryLabel);
      return false;
    }

    if (!nextHeader.weightCategoryKg) {
      appContext?.setNotificationData?.({
        message: 'Укажите значение для строки «весовая категория до __ кг»',
        type: NotificationType.ERROR,
      });
      setHeaderWeightKg(savedHeader.weightCategoryKg);
      return false;
    }

    if (!nextHeader.protocolDate) {
      appContext?.setNotificationData?.({
        message: 'Укажите дату протокола',
        type: NotificationType.ERROR,
      });
      setHeaderDate(savedHeader.protocolDate);
      return false;
    }

    if (
      nextHeader.participantCategoryLabel === savedHeader.participantCategoryLabel &&
      nextHeader.weightCategoryKg === savedHeader.weightCategoryKg &&
      nextHeader.protocolDate === savedHeader.protocolDate
    ) {
      return true;
    }

    setSavingHeader(true);
    try {
      await weighInProtocolClient.updateProtocolHeader({
        tournamentId: tournament.id!,
        ageCategoryInTournamentId: ageCategory.id!,
        weightCategoryInTournamentId: weightCategory.id!,
        participantCategoryLabel: nextHeader.participantCategoryLabel,
        weightCategoryKg: nextHeader.weightCategoryKg,
        protocolDate: nextHeader.protocolDate,
      });
      headerDirtyRef.current = false;
      setSavedHeader(nextHeader);
      setParticipantCategoryLabel(nextHeader.participantCategoryLabel);
      setHeaderWeightKg(nextHeader.weightCategoryKg);
      setHeaderDate(nextHeader.protocolDate);
      onParticipantsUpdated();
      if (showSuccess) {
        appContext?.setNotificationData?.({
          message: 'Шапка протокола сохранена',
          type: NotificationType.SUCCESS,
        });
      }
      return true;
    } catch (ex) {
      handleError(appContext!.setNotificationData, ex, 'Не удалось сохранить шапку протокола');
      setParticipantCategoryLabel(savedHeader.participantCategoryLabel);
      setHeaderWeightKg(savedHeader.weightCategoryKg);
      setHeaderDate(savedHeader.protocolDate);
      return false;
    } finally {
      setSavingHeader(false);
    }
  };

  const handleSaveHeader = () => {
    void persistProtocolHeader(true);
  };

  const handleClose = () => {
    if (savingKey !== null || bulkSaving || printing || savingHeader) return;
    onClose();
  };

  const handlePrint = async () => {
    setPrinting(true);
    try {
      if (!(await persistProtocolHeader())) return;
      if (entries.length > 0) {
        await weighInProtocolClient.saveEntries(buildSaveRequest(entries));
      }
      await weighInProtocolClient.printProtocol(
        tournament.id!,
        ageCategory.id!,
        weightCategory.id!
      );
    } catch (ex) {
      handleError(appContext!.setNotificationData, ex, 'Не удалось сформировать PDF протокола');
    } finally {
      setPrinting(false);
    }
  };

  const busy = savingKey !== null || bulkSaving || importing || printing || savingHeader;

  if (!isOpen) return null;

  return (
    <div className="weighin-modal-overlay" onClick={handleClose}>
      <div className="weighin-modal weighin-modal--protocol" onClick={(e) => e.stopPropagation()}>
        <div className="weighin-modal__header">
          <div>
            <h2>Протокол взвешивания</h2>
            <p className="weighin-modal__subtitle">
              {formatWeightCategoryLabel(weightCategory, tournament.weightCategories ?? [])}
            </p>
          </div>
          <button
            className="weighin-modal__close"
            onClick={handleClose}
            disabled={busy}
            aria-label="Закрыть"
          >
            ×
          </button>
        </div>

        <div className="weighin-modal__print-header-panel">
          <div className="weighin-modal__print-header-panel-title">Шапка для печати PDF</div>
          <label className="weighin-modal__print-header-main-field">
            <span className="weighin-modal__print-header-label">Заголовок протокола</span>
            <input
              className="weighin-modal__print-header-input weighin-modal__print-header-input--title"
              value={participantCategoryLabel}
              onChange={(e) => {
                headerDirtyRef.current = true;
                setParticipantCategoryLabel(e.target.value);
              }}
              onBlur={handleSaveHeader}
              disabled={busy}
              placeholder={`${WEIGH_IN_PROTOCOL_TITLE_PREFIX} …`}
              maxLength={250}
            />
            <span className="weighin-modal__print-header-hint">
              По умолчанию: {buildDefaultProtocolTitle(tournament)}. Можно дополнить или изменить для этой категории.
            </span>
          </label>
          <div className="weighin-modal__print-header-fields">
            <label className="weighin-modal__print-header-field">
              <span className="weighin-modal__print-header-label">Весовая категория, кг</span>
              <input
                className="weighin-modal__print-header-input"
                value={headerWeightKg}
                onChange={(e) => {
                  headerDirtyRef.current = true;
                  setHeaderWeightKg(e.target.value);
                }}
                onBlur={handleSaveHeader}
                disabled={busy}
                placeholder="40"
                maxLength={20}
              />
            </label>
            <label className="weighin-modal__print-header-field">
              <span className="weighin-modal__print-header-label">Дата протокола</span>
              <input
                type="date"
                className="weighin-modal__print-header-input"
                value={headerDate}
                onChange={(e) => {
                  headerDirtyRef.current = true;
                  setHeaderDate(e.target.value);
                }}
                onBlur={handleSaveHeader}
                disabled={busy}
              />
            </label>
          </div>
        </div>

        <div className="weighin-modal__toolbar">
          <button
            type="button"
            className="weighin-modal__btn weighin-modal__btn--secondary"
            onClick={handleImport}
            disabled={busy}
          >
            {importing ? 'Импорт…' : 'Импорт из заявленных'}
          </button>
          <button
            type="button"
            className="weighin-modal__btn weighin-modal__btn--secondary"
            onClick={() => setShowQuickAdd((v) => !v)}
            disabled={busy}
          >
            {showQuickAdd ? 'Скрыть форму' : '+ Добавить участника'}
          </button>
          <button
            type="button"
            className="weighin-modal__btn weighin-modal__btn--secondary"
            onClick={handlePrint}
            disabled={busy}
          >
            {printing ? 'PDF…' : 'Печать PDF'}
          </button>
          <button
            type="button"
            className="weighin-modal__btn weighin-modal__btn--primary"
            onClick={handleSaveAll}
            disabled={busy || entries.length === 0}
          >
            {bulkSaving ? 'Сохранение…' : 'Сохранить всё'}
          </button>
        </div>

        {showQuickAdd && (
          <div className="weighin-modal__quick-add">
            {/* Строка 1: ФИО, дата рождения, разряд, вес */}
            <div className="weighin-modal__quick-add-grid">
              <input
                placeholder="Фамилия *"
                value={quickAdd.lastName}
                onChange={(e) => setQuickAdd({ ...quickAdd, lastName: e.target.value })}
              />
              <input
                placeholder="Имя *"
                value={quickAdd.firstName}
                onChange={(e) => setQuickAdd({ ...quickAdd, firstName: e.target.value })}
              />
              <input
                placeholder="Отчество"
                value={quickAdd.patronymic ?? ''}
                onChange={(e) => setQuickAdd({ ...quickAdd, patronymic: e.target.value })}
              />
              <input
                type="date"
                value={quickAdd.birthDate}
                onChange={(e) => setQuickAdd({ ...quickAdd, birthDate: e.target.value })}
              />
              <select
                value={quickAdd.rank}
                onChange={(e) => setQuickAdd({ ...quickAdd, rank: e.target.value })}
              >
                {rankOptions.map((r) => (
                  <option key={r} value={r}>{r}</option>
                ))}
              </select>
              <input
                type="number"
                min="0"
                max="200"
                step="0.1"
                placeholder="Вес, кг *"
                value={quickAdd.weight || ''}
                onChange={(e) => setQuickAdd({ ...quickAdd, weight: parseFloat(e.target.value) || 0 })}
              />
            </div>
            {/* Строка 2: организация, тренер, пол */}
            <div className="weighin-modal__quick-add-grid" style={{ marginTop: 8 }}>
              <div className="weighin-modal__quick-add-select">
                <ReactSelectDefault<string>
                  options={organizationSelectOptions}
                  value={quickAdd.organization?.trim() || undefined}
                  onChange={(option) =>
                    setQuickAdd({ ...quickAdd, organization: option.value || undefined })
                  }
                  onClearChange={() => setQuickAdd({ ...quickAdd, organization: undefined })}
                  placeholder="Организация"
                  params={{ width: 100, height: 34 }}
                  isClearable
                  disabled={busy}
                  error={false}
                />
              </div>
              <div className="weighin-modal__quick-add-select">
                <ReactSelectDefault<number>
                  options={coachOptions}
                  value={quickAddCoachId}
                  onChange={(option) => setQuickAddCoachId(option?.value)}
                  onClearChange={() => setQuickAddCoachId(undefined)}
                  placeholder="Тренер"
                  params={{ width: 100, height: 34 }}
                  isClearable
                  disabled={busy}
                  error={false}
                />
              </div>
              <select
                value={quickAdd.gender}
                onChange={(e) => setQuickAdd({ ...quickAdd, gender: e.target.value as Gender })}
                disabled={!!(ageCategory.gender ?? weightCategory.gender)}
                title={ageCategory.gender ?? weightCategory.gender ? 'Пол задан категорией протокола' : undefined}
              >
                <option value={Gender.Male}>Мальчик</option>
                <option value={Gender.Female}>Девочка</option>
              </select>
              {(ageCategory.gender ?? weightCategory.gender) && (
                <span className="weighin-modal__quick-add-gender-hint">
                  Пол: {formatGenderLabel(ageCategory.gender ?? weightCategory.gender)}
                </span>
              )}
            </div>
            <button
              type="button"
              className="weighin-modal__btn weighin-modal__btn--primary"
              onClick={handleQuickAdd}
              disabled={busy}
            >
              Добавить в протокол
            </button>
          </div>
        )}

        {!loading && entries.length > 0 && (
          <div className="weighin-modal__search">
            <div className="weighin-modal__search-field">
              <input
                ref={searchInputRef}
                type="search"
                className="weighin-modal__search-input"
                placeholder="Поиск по ФИО"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                disabled={busy}
              />
            </div>
            <div className="weighin-modal__search-meta">
              {searchQuery.trim() ? (
                <>Найдено: <strong>{filteredEntries.length}</strong> из {entries.length}</>
              ) : (
                <>Записей: {entries.length}</>
              )}
            </div>
          </div>
        )}

        <div className="weighin-modal__body">
          {loading ? (
            <div className="weighin-modal__loading">Загрузка протокола…</div>
          ) : entries.length === 0 ? (
            <div className="weighin-modal__empty">
              Протокол пуст. Импортируйте заявленных участников или добавьте вручную.
            </div>
          ) : filteredEntries.length === 0 ? (
            <div className="weighin-modal__empty">По запросу никого не найдено.</div>
          ) : (
            <div className="weighin-modal__table-wrap">
              <table className="weighin-modal__table">
                <thead>
                  <tr>
                    <th>Фамилия</th>
                    <th>Имя</th>
                    <th>Отчество</th>
                    <th>Дата рожд.</th>
                    <th>Разряд</th>
                    <th>Вес</th>
                    <th>Организация</th>
                    <th>Тренер</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  {filteredEntries.map((entry) => {
                    const err = rowErrors[entry._key];
                    const saving = savingKey === entry._key;
                    return (
                      <tr key={entry._key}>
                        <td>
                          <input
                            className="weighin-modal__cell-input"
                            value={entry.lastName}
                            onChange={(e) => updateEntry(entry._key, { lastName: e.target.value })}
                            disabled={busy}
                          />
                        </td>
                        <td>
                          <input
                            className="weighin-modal__cell-input"
                            value={entry.firstName}
                            onChange={(e) => updateEntry(entry._key, { firstName: e.target.value })}
                            disabled={busy}
                          />
                        </td>
                        <td>
                          <input
                            className="weighin-modal__cell-input"
                            value={entry.patronymic ?? ''}
                            onChange={(e) => updateEntry(entry._key, { patronymic: e.target.value })}
                            disabled={busy}
                          />
                        </td>
                        <td>
                          <input
                            type="date"
                            className="weighin-modal__cell-input"
                            value={entry.birthDate}
                            onChange={(e) => updateEntry(entry._key, { birthDate: e.target.value })}
                            disabled={busy}
                          />
                        </td>
                        <td>
                          <select
                            className="weighin-modal__cell-input"
                            value={entry.rank}
                            onChange={(e) => updateEntry(entry._key, { rank: e.target.value })}
                            disabled={busy}
                          >
                            {rankOptions.map((r) => (
                              <option key={r} value={r}>{r}</option>
                            ))}
                          </select>
                        </td>
                        <td>
                          <input
                            type="number"
                            min="0"
                            max="200"
                            step="0.1"
                            className={`weighin-modal__cell-input weighin-modal__cell-input--weight ${err ? 'weighin-modal__weight-input--error' : ''}`}
                            value={entry.weight}
                            onChange={(e) =>
                              updateEntry(entry._key, { weight: parseFloat(e.target.value) || 0 })
                            }
                            disabled={busy}
                          />
                        </td>
                        <td className="weighin-modal__cell-select">
                          <ReactSelectDefault<string>
                            options={organizationSelectOptions}
                            value={entry.organization?.trim() || undefined}
                            onChange={(option) =>
                              updateEntry(entry._key, { organization: option.value || undefined })
                            }
                            onClearChange={() =>
                              updateEntry(entry._key, { organization: undefined })
                            }
                            placeholder="Организация"
                            params={{ width: 100, height: 34 }}
                            isClearable
                            disabled={busy}
                            error={false}
                          />
                        </td>
                        <td className="weighin-modal__cell-select">
                          <ReactSelectDefault<number>
                            options={coachOptions}
                            value={entry.coachId}
                            onChange={(option) => {
                              const coach = coaches.find((c) => c.id === option?.value);
                              updateEntry(entry._key, {
                                coachId: option?.value,
                                coachName: coach ? formatCoachName(coach) : undefined,
                              });
                            }}
                            onClearChange={() =>
                              updateEntry(entry._key, { coachId: undefined, coachName: undefined })
                            }
                            placeholder="Нет тренера"
                            params={{ width: 100, height: 34 }}
                            isClearable
                            disabled={busy}
                            error={false}
                          />
                        </td>
                        <td className="weighin-modal__actions-cell">
                          <div className="weighin-modal__actions-row">
                            <button
                              type="button"
                              className="weighin-modal__save-btn"
                              onClick={() => handleSaveRow(entry)}
                              disabled={busy}
                            >
                              {saving ? '…' : 'Сохранить'}
                            </button>
                          {entry.id && (    
                              <button
                                type="button"
                                className="weighin-modal__remove-btn"
                                onClick={() => handleRemove(entry)}
                                disabled={busy}
                                title="Удалить из протокола"
                              >
                                ×
                              </button>
                            )}
                          </div>
                          {err && <div className="weighin-modal__row-error">{err}</div>}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {confirm && (
        <div className="weighin-modal__confirm-overlay" onClick={(e) => e.stopPropagation()}>
          <div className="weighin-modal__confirm">
            <h3>Перевод в другую категорию</h3>
            <p>
              Вес <strong>{confirm.newWeight} кг</strong> не попадает в текущую категорию ({categoryLabel}).
            </p>
            <p>
              Перевести участника в категорию{' '}
              <strong>
                {formatWeightCategoryLabel(confirm.targetCategory, tournament.weightCategories ?? [])}
              </strong>
              ?
            </p>
            <div className="weighin-modal__confirm-actions">
              <button
                className="weighin-modal__btn weighin-modal__btn--secondary"
                onClick={() => setConfirm(null)}
              >
                Отмена
              </button>
              <button
                className="weighin-modal__btn weighin-modal__btn--primary"
                onClick={handleConfirmReassign}
              >
                Перевести
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default WeighInModal;
